import React, { useEffect, useImperativeHandle, useState } from "react"
import { CardElement } from "@stripe/react-stripe-js"
import PropTypes from "prop-types"
import FormField from "app/components/sdk/form_field"

import colors from "foundation/_colors_export.scss?variables"
import typography from "foundation/_typography_export.scss?variables"
import responsive from "foundation/_responsive_export.scss?variables"

// above this size the CC # is partially hidden after entry
const DEFAULT_FONT_SIZE = "22.2px"

export const CardElementOptions = {
  hidePostalCode: true,
  style: {
    base: {
      color: colors.primaryTextColor,
      fontFamily: typography["avenir"],
      fontSize: DEFAULT_FONT_SIZE,
      fontWeight: 600,
      "::placeholder": {
        color: "#ccc",
      },
    },
    invalid: {
      color: "#ee4433",
      iconColor: "#ee4433",
    },
  },
}

const noop = () => {}

const CreditCardField = React.forwardRef((props, ref) => {
  const { onCardReadyChange, errors } = props

  const [cardInputComplete, setCardInputComplete] = useState(false)
  const [validationErrors, setValidationErrors] = useState(null)

  useEffect(() => {
    const windowWidth =
      window.innerWidth || document.documentElement.clientWidth
    let fontSize

    if (windowWidth < 370) {
      fontSize = "14.4px"
      // ^ our regular font size makes the card numbers overlap the exp data and cvc
    } else if (windowWidth < parseInt(responsive["screenXsMin"])) {
      fontSize = "16.8px"
      // ^ *
    } else if (windowWidth < parseInt(responsive["screenSmMin"])) {
      fontSize = "19.2px"
    } else if (windowWidth < parseInt(responsive["screenLgMin"])) {
      fontSize = "21.6px"
    } else {
      fontSize = DEFAULT_FONT_SIZE
    }

    CardElementOptions.style.base.fontSize = fontSize
  }, [])

  // An event is complete if there are no errors and all fields have valid values.
  // If an event is not complete, but has no errors, it means there are missing values.
  // We use complete to notify if the input is ready for submission.
  const handleChange = event => {
    if (event.error) {
      setValidationErrors({ cardnumber: [event.error.message] })
    } else {
      setValidationErrors(null)
    }
    setCardInputComplete(event.complete)
    onCardReadyChange(event.complete)
  }

  const handleOnBlur = () => {
    if (!cardInputComplete && !validationErrors?.cardnumber) {
      setValidationErrors({ cardnumber: ["Missing required fields."] })
    }
  }

  const autoFocus = element => element.focus()

  useImperativeHandle(ref, () => ({
    getElement: () => {
      return CardElement
    },
  }))

  return (
    <div className="credit-card-field stripe">
      <FormField
        name="cardnumber"
        errors={errors?.cardnumber || validationErrors?.cardnumber}
        animateErrors={true}
        ariaLabel="credit card field"
      >
        <CardElement
          options={CardElementOptions}
          className="input"
          onChange={handleChange}
          onBlur={handleOnBlur}
          onReady={autoFocus}
        />
      </FormField>
    </div>
  )
})

CreditCardField.propTypes = {
  onCardReadyChange: PropTypes.func,
  errors: PropTypes.object,
}

CreditCardField.defaultProps = {
  onCardError: noop,
  onCardReady: noop,
  errors: {},
}

CreditCardField.displayName = "CreditCardField"
export default CreditCardField
